import { inject } from '@angular/core';
import { AuthPipeGenerator, } from '@angular/fire/auth-guard';
import { CanActivateFn, Router, Routes } from '@angular/router';

import { canActivate, redirectLoggedInTo, redirectUnauthorizedTo } from '@angular/fire/auth-guard';
import { passwordChangeGuard } from './guards/password-change.guard';
import { roleGuard } from './guards/role.guard';
import { storeModeGuard } from './guards/store-mode.guard';
import { AuthService } from './services/auth/auth.service';
import { SessionExpirationGuard } from './session-expiration/session-expiration.guard';

const redirectToHome: AuthPipeGenerator = () => redirectLoggedInTo(['inicio']);
const redirectToLogin: AuthPipeGenerator = () => redirectUnauthorizedTo(['login']);
const allowLogoutTransition: CanActivateFn = () => {
  const authService = inject(AuthService);

  return authService.logoutInProgress
    ? true
    : inject(Router).createUrlTree(['/login']);
};

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full'
  },
  {
    path: 'login',
    ...canActivate(redirectToHome),
    loadComponent: () => import('./login/login.page').then(m => m.LoginRegisterPage),
  },
  {
    path: 'cerrando-sesion',
    canActivate: [allowLogoutTransition],
    loadComponent: () => import('./logout-transition/logout-transition.page').then(m => m.LogoutTransitionPage)
  },
  {
    path: 'restablecer-contrasena',
    loadComponent: () => import('./reset-password/reset-password.page').then(m => m.ResetPasswordPage)
  },
  {
    path: 'cambiar-contrasena',
    ...canActivate(redirectToLogin),
    loadComponent: () => import('./change-password/change-password.page').then(m => m.ChangePasswordPage)
  },
  {
    path: 'olvide-contrasena',
    loadComponent: () => import('./forgot-password/forgot-password.page').then(m => m.ForgotPasswordPage)
  },
  {
    path: '',
    ...canActivate(redirectToLogin),
    canActivateChild: [SessionExpirationGuard, passwordChangeGuard, storeModeGuard],
    children: [
      {
        path: 'areas',
        canActivate: [roleGuard],
        data: { allowedRoles: ['superadmin', 'areas_r', 'areas_p'] },
        loadComponent: () => import('./areas/areas.page').then(m => m.AreasPage)
      },
      {
        path: 'graficos/:deviceId',
        canActivate: [roleGuard],
        data: { allowedRoles: ['superadmin', 'areas_r', 'areas_p'] },
        loadComponent: () => import('./new-area/new-area.page').then(m => m.NewAreaPage)
      },
      {
        path: 'alertas',
        canActivate: [roleGuard],
        data: { allowedRoles: ['superadmin', 'alerts_r', 'alerts_rw'] },
        loadComponent: () => import('./alerts/alerts.page').then(m => m.AlertsPage)
      },
      {
        path: 'alertas/configuracion',
        canActivate: [roleGuard],
        data: { allowedRoles: ['superadmin', 'alerts_rw'] },
        loadComponent: () => import('./configure-alerts/configure-alerts.page').then(m => m.ConfigureAlertPage)
      },
      {
        path: 'usuarios',
        canActivate: [roleGuard],
        data: { allowedRoles: ['superadmin', 'users_r', 'users_rw'] },
        loadComponent: () => import('./users/users.page').then(m => m.UsersPage)
      },
      {
        path: 'organizaciones',
        canActivate: [roleGuard],
        data: { allowedRoles: ['superadmin'] },
        loadComponent: () => import('./organizations/organizations.page').then(m => m.OrganizationsPage)
      },
      {
        path: 'proximamente',
        loadComponent: () => import('./soon/soon.page').then(m => m.SoonPage)
      },
      {
        path: 'perfil/:username',
        loadComponent: () => import('./profile/profile.page').then(m => m.ProfilePage)
      },
      {
        path: 'manuales',
        canActivate: [roleGuard],
        data: { allowedRoles: ['superadmin', 'manuals_r'] },
        loadComponent: () => import('./manuals/manuals.page').then(m => m.ManualsPage)
      },
      {
        path: 'dispositivos',
        canActivate: [roleGuard],
        data: { allowedRoles: ['superadmin', 'devices_r', 'devices_rw'] },
        loadComponent: () => import('./devices/devices.page').then(m => m.DevicesPage)
      },
      {
        path: 'inicio',
        loadComponent: () => import('./start/start.page').then(m => m.StartPage)
      },
      {
        path: 'soporte',
        canActivate: [roleGuard],
        data: { allowedRoles: ['support_rw'] },
        loadComponent: () => import('./support/support.page').then(m => m.SupportPage)
      },
      {
        path: 'facturacion',
        canActivate: [roleGuard],
        data: { allowedRoles: ['superadmin', 'billing_r'] },
        loadComponent: () => import('./billing/billing.page').then( m => m.BillingPage)
      },
      {
        path: 'logs-usuarios',
        canActivate: [roleGuard],
        data: { allowedRoles: ['superadmin', 'user_logs'] },
        loadComponent: () => import('./user-logs/user-logs.page').then( m => m.UserLogsPage)
      },
      {
        path: 'historial-cambios',
        loadComponent: () => import('./changelog/changelog.page').then(m => m.ChangelogPage)
      },
      {
        path: 'organizacion-perfil/nueva',
        canActivate: [roleGuard],
        data: { allowedRoles: ['superadmin'] },
        loadComponent: () => import('./organization-profile/organization-profile.page').then(m => m.OrganizationProfilePage)
      },
      {
        path: 'organizacion-perfil/:name',
        canActivate: [roleGuard],
        data: { allowedRoles: ['superadmin'] },
        loadComponent: () => import('./organization-profile/organization-profile.page').then(m => m.OrganizationProfilePage)
      }
    ]
  },
  {
    path: '**',
    redirectTo: 'login'
  }
];
